import { useCallback, useEffect, useRef } from "react";

import { useSelector } from "react-redux";

import {
  selectMondayAccountId,
  selectMondayBoardId,
  selectMondayItemId,
  selectMondayUserId,
} from "@state/mondaySlice";

import { signPayload } from "@utils/jwt";
import logger from "@utils/logger";

const API_URL = import.meta.env.VITE_API_URL;

export const useOAuthFlow = (provider: "monday" | "google", onSuccess?: () => void) => {
  const oauthWindowRef = useRef<Window | null>(null);

  const mondayAccountId = useSelector(selectMondayAccountId);
  const mondayUserId = useSelector(selectMondayUserId);
  const mondayItemId = useSelector(selectMondayItemId);
  const mondayBoardId = useSelector(selectMondayBoardId);

  useEffect(() => {
    const messageHandler = (event: MessageEvent) => {
      logger.debug("popup message event", event);

      const expectedMessage = `${provider}_connected`;

      if (event.origin === API_URL && event.data === expectedMessage) {
        logger.debug("OAuth success:", event.origin, event.data);

        if (oauthWindowRef.current && !oauthWindowRef.current.closed) {
          oauthWindowRef.current.close();
        }

        onSuccess?.();
      }
    };

    window.addEventListener("message", messageHandler);
    return () => window.removeEventListener("message", messageHandler);
  }, [onSuccess, provider]);

  const startOAuthFlow = useCallback(async () => {
    if (!mondayUserId || !mondayAccountId) {
      logger.error("Missing mondayUserId or mondayAccountId");
      return;
    }

    const state =
      provider === "google"
        ? await signPayload({ mondayUserId, mondayAccountId, mondayItemId, mondayBoardId })
        : await signPayload({ mondayUserId, mondayAccountId });

    const authorizationUrl = `${API_URL}/api/oauth/${provider}/connect?state=${encodeURIComponent(state)}`;

    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const oauthWindow = window.open(
      authorizationUrl,
      `Login with ${provider}`,
      `width=${width},height=${height},top=${top},left=${left}`,
    );

    if (!oauthWindow) {
      logger.error("OAuth popup blocked. Please enable pop-ups.");
      return;
    }

    oauthWindowRef.current = oauthWindow;
  }, [provider, mondayUserId, mondayAccountId]);

  return startOAuthFlow;
};
